/**
 * Product deal card — matches Dashboard V2 system design product card styling.
 */
import React, { useMemo, useState } from 'react';
import {
    BatteryHigh,
    CurrencyDollar,
    Eye,
    House,
    Lock,
    SunHorizon,
    X,
} from '@phosphor-icons/react';
import { PreContractLivePricingModal } from './PreContractLivePricingModal';
import { PreContractProductPricingModal } from './PreContractProductPricingModal';
import type { PreContractProduct, PreContractIntakeForm } from '../lib/preContractTypes';
import type { SolarDesignRevision } from '../lib/preContractSolarDesign';
import type { PreContractSummaryItem } from './PreContractModuleStep.types';
import { useOneButtonStore } from '../../store/oneButtonStore';
import {
    buildPreContractRoofDesignSnapshot,
    formatRoofPricePerSqft,
} from '../lib/preContractRoofDesign';
import {
    buildPreContractLivePpwQuotes,
    formatPpaRate,
    pickRecommendedPartnerQuote,
    resolveLivePartnerQuote,
    type PreContractPpwMetricsOverride,
} from '../lib/preContractPpwPricing';
import {
    type PreContractDesignPartnerRecommendation,
    type PreContractPartnerSyncState,
    type PreContractProductPricingOption,
} from '../lib/preContractProductPartners';
import {
    preContractSolarLiveHints,
    resolvePreContractSolarMetrics,
} from '../lib/preContractSolarDesignMetrics';
import { countIncludedModules } from '../lib/preContractModuleLayout';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ProductionsCardMergedSystemSection.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/OneButtonUxRefresh.css';

export type PreContractProductDealCardProps = {
    product: PreContractProduct;
    form: PreContractIntakeForm;
    icon: React.ReactNode;
    accent: string;
    label: string;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    summaryItems?: PreContractSummaryItem[];
    onOpenDesign: () => void;
    designActionDisabled?: boolean;
    designActionDisabledReason?: string | null;
    onRemove: () => void;
    designPartner?: PreContractDesignPartnerRecommendation | null;
    savedPartner?: string | null;
    savedMethod?: string | null;
    partnerSyncState?: PreContractPartnerSyncState;
    partnerSyncing?: boolean;
    pricingLoading?: boolean;
    onSyncPartner?: () => void;
    partnerSyncDisabled?: boolean;
    /** Opens the V2 Products / finance-partner hub modal for this product. */
    onOpenFinanceSetup?: () => void;
    financeSetupLocked?: boolean;
    financeSetupLockedReason?: string | null;
    pricingOptions?: PreContractProductPricingOption[];
    selectedPricingOption?: PreContractProductPricingOption | null;
    onSelectPricingOption?: (option: PreContractProductPricingOption) => void;
    preferEnfinPartner?: boolean;
    ppaRateLoading?: boolean;
    onOpenPpaRates?: () => void;
    designRevision?: SolarDesignRevision | null;
    designModified?: boolean;
    appliedDesignLabel?: string | null;
};

type ProductKind = 'solar' | 'roof' | 'battery' | 'other';

type MetricCell = {
    label: string;
    value: string;
    emphasis?: boolean;
    multiplier?: boolean;
    /** Live oneDRAW value shown beside the saved design value when the two disagree. */
    liveHint?: string | null;
    onClick?: () => void;
};

const PRODUCT_ICONS = {
    solar: SunHorizon,
    roof: House,
    battery: BatteryHigh,
} as const;

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function formatRatePerKwh(raw: string | number | null | undefined): string {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) && raw > 0 ? formatPpaRate(raw) : '—';
    }
    const n = Number(String(raw ?? '').replace(/[$,\s]/g, '').trim());
    return Number.isFinite(n) && n > 0 ? formatPpaRate(n) : '—';
}

/** A rendered metric counts as available only when it carries a non-zero number. */
function metricValueAvailable(value: string | null | undefined): boolean {
    const text = String(value ?? '').trim();
    if (!text || text === '—' || text === '-' || text.toUpperCase() === 'N/A') return false;
    return /[1-9]/.test(text);
}

function displayProductName(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    if (product === 'RR') return 'Remove & reinstall';
    return product;
}

function productKind(product: PreContractProduct): ProductKind {
    if (product === 'Solar') return 'solar';
    if (product === 'Roof') return 'roof';
    if (product === 'Battery') return 'battery';
    return 'other';
}

function cardTitle(product: PreContractProduct): string {
    if (product === 'Solar') return 'Solar system';
    return displayProductName(product);
}

function MetricCellView({ metric }: { metric: MetricCell }) {
    const className = `obd-sd-card__metric${metric.emphasis ? ' obd-sd-card__metric--emphasis' : ''}${
        metric.multiplier ? ' obd-sd-card__metric--multiplier' : ''
    }${metric.onClick ? ' obd-sd-card__metric--action' : ''}`;
    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        metric.onClick?.();
    };
    const value = (
        <dd className="obd-sd-card__metric-value">
            {metric.value}
            {metric.liveHint ? (
                <span
                    className="obd-sd-card__metric-live-hint"
                    title="Live oneDRAW value — finalize the design to match"
                >
                    ({metric.liveHint})
                </span>
            ) : null}
        </dd>
    );
    if (metric.onClick) {
        return (
            <button type="button" className={className} onClick={handleClick}>
                <dt className="obd-sd-card__metric-label">{metric.label}</dt>
                {value}
            </button>
        );
    }
    return (
        <div className={className}>
            <dt className="obd-sd-card__metric-label">{metric.label}</dt>
            {value}
        </div>
    );
}

function MetricsGrid({ metrics }: { metrics: MetricCell[] }) {
    return (
        <div className="obd-sd-card__metrics-panel">
            <dl className="obd-sd-card__metrics">
                {metrics.map((metric) => (
                    <MetricCellView key={metric.label} metric={metric} />
                ))}
            </dl>
        </div>
    );
}

export const PreContractProductDealCard: React.FC<PreContractProductDealCardProps> = ({
    product,
    form,
    accent,
    label,
    metricsOverride = null,
    summaryItems = [],
    onOpenDesign,
    designActionDisabled = false,
    designActionDisabledReason = null,
    onRemove,
    designPartner = null,
    savedPartner = null,
    savedMethod = null,
    partnerSyncState = 'unset',
    partnerSyncing = false,
    pricingLoading = false,
    onSyncPartner,
    partnerSyncDisabled = false,
    onOpenFinanceSetup,
    financeSetupLocked = false,
    financeSetupLockedReason = null,
    pricingOptions = [],
    selectedPricingOption = null,
    onSelectPricingOption,
    preferEnfinPartner = false,
    ppaRateLoading = false,
    onOpenPpaRates,
    designRevision = null,
    designModified = false,
    appliedDesignLabel = null,
}) => {
    const [compareModalOpen, setCompareModalOpen] = useState(false);
    const [livePricingModalOpen, setLivePricingModalOpen] = useState(false);
    const kind = productKind(product);
    const Icon = PRODUCT_ICONS[kind as keyof typeof PRODUCT_ICONS] ?? SunHorizon;
    const showPricingExplorer =
        product === 'Solar' || product === 'Roof' || product === 'Battery';
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);

    const solarMetrics = useMemo(() => {
        if (product !== 'Solar') return null;
        return resolvePreContractSolarMetrics({ form, systemDesign, metricsOverride });
    }, [form, metricsOverride, product, systemDesign]);

    const solarLiveHints = useMemo(() => {
        if (!solarMetrics) return { systemKw: null, multiplier: null };
        const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
        return preContractSolarLiveHints(solarMetrics, count);
    }, [form.roofModuleLayouts, form.roofSelections, solarMetrics]);

    const solarSnapshot = useMemo(() => {
        if (product !== 'Solar') return null;
        const escalator = selectedPricingOption?.escalator ?? 0.0299;
        const result = buildPreContractLivePpwQuotes(form, escalator, null, solarMetrics);
        const bestQuote = pickRecommendedPartnerQuote(result.quotes, {
            preferEnfin: preferEnfinPartner,
        });
        return { result, bestQuote };
    }, [form, preferEnfinPartner, product, selectedPricingOption?.escalator, solarMetrics]);

    const roofSnapshot = useMemo(() => {
        if (product !== 'Roof') return null;
        return buildPreContractRoofDesignSnapshot({
            form,
            systemDesign,
            priceQuoteOverrides,
        });
    }, [form, priceQuoteOverrides, product, systemDesign]);

    const batterySnapshot = useMemo(() => {
        if (product !== 'Battery') return null;
        return {
            manufacturer: form.batteryManufacturer.trim() || 'Battery',
            name: form.batteryName.trim() || 'Not selected',
            qty: form.batteryQuantity > 0 ? form.batteryQuantity : null,
        };
    }, [form, product]);

    const metricByLabel = (metricLabel: string) =>
        summaryItems.find((item) => item.label === metricLabel)?.value ?? '—';

    // Prefer the partner already tied to the opportunity product; fall back to the
    // selected / recommended pricing option only when nothing is saved yet.
    const financePartner =
        savedPartner?.trim() ||
        designPartner?.partner?.trim() ||
        'Partner not set';
    const financeMethod =
        (savedPartner?.trim()
            ? savedMethod?.trim() || designPartner?.financeMethod?.trim()
            : designPartner?.financeMethod?.trim() || savedMethod?.trim()) ||
        '';

    const subtitle = useMemo(() => {
        if (product === 'Solar' && solarSnapshot) {
            const monthly =
                designPartner?.monthly != null && designPartner.monthly > 0
                    ? formatMoney(designPartner.monthly, 0)
                    : solarSnapshot.bestQuote?.estimatedMonthly != null
                      ? formatMoney(solarSnapshot.bestQuote.estimatedMonthly, 0)
                      : null;
            if (monthly) return `${monthly}/mo`;
            if (solarMetrics?.annualProductionKwh) {
                return `${Math.round(solarMetrics.annualProductionKwh).toLocaleString()} kWh/yr`;
            }
            return label;
        }
        if (product === 'Roof' && roofSnapshot?.configured && roofSnapshot.shingleColor) {
            return roofSnapshot.shingleColor;
        }
        if (product === 'Battery' && batterySnapshot?.qty) {
            return `${batterySnapshot.qty}× ${batterySnapshot.name}`;
        }
        return label;
    }, [
        batterySnapshot,
        designPartner?.monthly,
        label,
        product,
        roofSnapshot,
        solarMetrics,
        solarSnapshot,
    ]);

    const solarLiveQuote = useMemo(() => {
        if (product !== 'Solar') return null;
        return (
            resolveLivePartnerQuote(
                form,
                selectedPricingOption?.partner ?? designPartner?.partner,
                {
                    escalator: selectedPricingOption?.escalator ?? 0.0299,
                    metricsOverride: solarMetrics,
                    preferEnfin: preferEnfinPartner,
                },
            ) ?? solarSnapshot?.bestQuote ?? null
        );
    }, [
        designPartner?.partner,
        form,
        preferEnfinPartner,
        product,
        selectedPricingOption?.escalator,
        selectedPricingOption?.partner,
        solarMetrics,
        solarSnapshot?.bestQuote,
    ]);

    const metrics = useMemo((): MetricCell[] => {
        if (product === 'Solar' && solarSnapshot) {
            const ppw = solarLiveQuote?.ppw ?? null;
            const epc = solarLiveQuote?.epc ?? null;
            return [
                {
                    label: 'System size',
                    value: solarMetrics?.systemKw
                        ? `${solarMetrics.systemKw.toFixed(2)} kW`
                        : '—',
                    liveHint: solarLiveHints.systemKw,
                },
                {
                    label: 'Production',
                    value: solarMetrics?.annualProductionKwh
                        ? `${Math.round(solarMetrics.annualProductionKwh).toLocaleString()} kWh`
                        : metricByLabel('System Production'),
                },
                {
                    label: 'Multiplier',
                    value: solarMetrics?.multiplier
                        ? String(Number(solarMetrics.multiplier.toFixed(3)))
                        : '—',
                    multiplier: true,
                    liveHint: solarLiveHints.multiplier,
                },
                { label: 'Annual usage', value: metricByLabel('Utility Usage') },
                {
                    label: 'All-in rate ($/kWh)',
                    value: formatRatePerKwh(form.utilityRate || solarSnapshot.result.allInRate),
                    emphasis: true,
                },
                {
                    label: 'Target/Min PPA Rate',
                    value: ppaRateLoading
                        ? 'Loading rate…'
                        : formatRatePerKwh(form.targetMinPpaRate),
                    emphasis: true,
                    onClick:
                        !ppaRateLoading && onOpenPpaRates ? onOpenPpaRates : undefined,
                },
                { label: 'PPW', value: formatPpw(ppw), emphasis: true },
                { label: 'EPC', value: formatMoney(epc, 0), emphasis: true },
            ];
        }
        if (product === 'Roof') {
            if (!roofSnapshot?.configured) {
                return [{ label: 'Status', value: 'Not configured' }];
            }
            const monthlyLabel =
                designPartner?.monthly != null && designPartner.monthly > 0
                    ? formatMoney(designPartner.monthly, 0)
                    : '—';
            return [
                {
                    label: 'Roof squares',
                    value: roofSnapshot.total > 0 ? `${roofSnapshot.total} sq` : '—',
                },
                {
                    label: 'Price / sq ft',
                    value: formatRoofPricePerSqft(roofSnapshot.pricePerSqft) ?? '—',
                    emphasis: true,
                },
                { label: 'Monthly', value: monthlyLabel },
                {
                    label: 'Low slope',
                    value: roofSnapshot.low > 0 ? `${roofSnapshot.low} sq` : '—',
                },
                {
                    label: 'Normal slope',
                    value: roofSnapshot.normal > 0 ? `${roofSnapshot.normal} sq` : '—',
                },
                { label: 'Shingle', value: roofSnapshot.shingleColor ?? '—' },
            ];
        }
        if (product === 'Battery' && batterySnapshot) {
            if (!batterySnapshot.qty) {
                return [{ label: 'Status', value: 'Not selected' }];
            }
            const monthlyLabel =
                designPartner?.monthly != null && designPartner.monthly > 0
                    ? formatMoney(designPartner.monthly, 0)
                    : '—';
            return [
                { label: 'Count', value: `${batterySnapshot.qty} units`, emphasis: true },
                { label: 'Monthly', value: monthlyLabel },
                { label: 'Brand', value: batterySnapshot.manufacturer },
                { label: 'Model', value: batterySnapshot.name },
            ];
        }
        return [{ label: 'Product', value: displayProductName(product) }];
    }, [
        batterySnapshot,
        designPartner?.monthly,
        form.targetMinPpaRate,
        form.utilityRate,
        onOpenPpaRates,
        ppaRateLoading,
        product,
        roofSnapshot,
        solarLiveHints,
        solarLiveQuote,
        solarMetrics,
        solarSnapshot,
        summaryItems,
    ]);

    const pricingLockReason = useMemo((): string | null => {
        if (product !== 'Solar') return null;
        const hasSystemSize = (solarMetrics?.systemKw ?? 0) > 0;
        const productionFallback =
            summaryItems.find((item) => item.label === 'System Production')?.value ?? '—';
        const hasProduction =
            (solarMetrics?.annualProductionKwh ?? 0) > 0 ||
            metricValueAvailable(productionFallback);
        if (hasSystemSize && hasProduction) return null;
        const missing = [
            hasSystemSize ? null : 'system size',
            hasProduction ? null : 'production',
        ].filter(Boolean);
        return `Pricing is locked until ${missing.join(' and ')} ${
            missing.length > 1 ? 'are' : 'is'
        } available. Complete the design first.`;
    }, [product, solarMetrics, summaryItems]);

    const pricingLocked = pricingLockReason != null;

    const bodyWarning = (() => {
        if (designActionDisabledReason) return designActionDisabledReason;
        if (product === 'Solar' && solarSnapshot?.bestQuote?.available === false) {
            return solarSnapshot.bestQuote.reason ?? 'Pricing unavailable';
        }
        if (pricingLoading && (product === 'Roof' || product === 'Battery')) {
            return 'Getting pricing…';
        }
        if (designPartner?.available === false) {
            const reason = designPartner.reason?.trim() || '';
            if (
                /pricing inputs are incomplete/i.test(reason) &&
                ((product === 'Roof' && (roofSnapshot?.total ?? 0) > 0) ||
                    (product === 'Battery' && (batterySnapshot?.qty ?? 0) > 0))
            ) {
                return null;
            }
            return reason || null;
        }
        return null;
    })();

    const named = appliedDesignLabel?.trim() || null;
    const revisionLabel = named
        ? designRevision === 'ai' && !named.startsWith('Design :')
            ? `AI · ${named}`
            : named
        : designRevision === 'ai'
          ? 'AI design applied'
          : designRevision === 'manual'
            ? 'Manual layout'
            : designModified
              ? 'Updated layout'
              : null;

    const openPricing = () => {
        if (pricingLocked) return;
        if (product === 'Solar') {
            setLivePricingModalOpen(true);
            return;
        }
        setCompareModalOpen(true);
    };

    return (
        <article
            role="listitem"
            className={`obd-sd-card obd-sd-card--${kind}${
                designModified ? ' pci-product-deal-card--modified' : ''
            }${designActionDisabled ? ' pci-product-deal-card--disabled' : ''}`}
            aria-label={`${displayProductName(product)} product`}
        >
            <header className="obd-sd-card__head">
                <div className="obd-sd-card__head-main">
                    <span className="obd-sd-card__icon" aria-hidden>
                        <Icon size={20} weight="duotone" />
                    </span>
                    <div className="obd-sd-card__head-text">
                        <h3 className="obd-sd-card__title">{cardTitle(product)}</h3>
                        <p className="obd-sd-card__subtitle">{subtitle}</p>
                        {revisionLabel ? (
                            <span className="obd-sd-card__fallback-flag">{revisionLabel}</span>
                        ) : null}
                    </div>
                </div>
                <div className="obd-sd-card__head-end">
                    <button
                        type="button"
                        className="pci-product-deal-card__remove"
                        aria-label={`Remove ${displayProductName(product)}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove();
                        }}
                    >
                        <X size={11} weight="bold" />
                    </button>
                </div>
            </header>

            <div className="obd-sd-card__body">
                {bodyWarning ? (
                    <p className="obd-sd-card__warning" role="status">
                        {bodyWarning}
                    </p>
                ) : null}
                <MetricsGrid metrics={metrics} />
            </div>

            <footer className="obd-sd-card__foot">
                <div className="obd-sd-card__actions" role="group" aria-label="Product actions">
                    <button
                        type="button"
                        className="obd-sd-card__action"
                        disabled={designActionDisabled}
                        title={designActionDisabledReason ?? undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (designActionDisabled) return;
                            onOpenDesign();
                        }}
                    >
                        <Eye size={15} weight="regular" aria-hidden />
                        Design
                    </button>
                    {showPricingExplorer ? (
                        <button
                            type="button"
                            className="obd-sd-card__action"
                            disabled={pricingLocked}
                            title={pricingLockReason ?? undefined}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (pricingLocked) return;
                                openPricing();
                            }}
                        >
                            {pricingLocked ? (
                                <Lock size={15} weight="bold" aria-hidden />
                            ) : (
                                <CurrencyDollar size={15} weight="bold" aria-hidden />
                            )}
                            Pricing
                        </button>
                    ) : null}
                </div>
            </footer>

            {showPricingExplorer ? (
                <>
                    <PreContractLivePricingModal
                        open={product === 'Solar' && livePricingModalOpen && !pricingLocked}
                        form={form}
                        metricsOverride={solarMetrics ?? metricsOverride}
                        preferEnfinPartner={preferEnfinPartner}
                        onClose={() => setLivePricingModalOpen(false)}
                    />
                    <PreContractProductPricingModal
                        open={compareModalOpen}
                        product={product}
                        accent={accent}
                        icon={<Icon size={20} weight="duotone" />}
                        options={pricingOptions}
                        selectedOption={selectedPricingOption}
                        designPartner={designPartner}
                        savedPartner={savedPartner}
                        savedMethod={savedMethod}
                        onClose={() => setCompareModalOpen(false)}
                        onSelect={(option) => onSelectPricingOption?.(option)}
                        onSync={
                            onSyncPartner
                                ? () => {
                                      onSyncPartner();
                                  }
                                : undefined
                        }
                        syncDisabled={
                            partnerSyncDisabled ||
                            financeSetupLocked ||
                            partnerSyncState === 'match' ||
                            !designPartner?.available
                        }
                        syncLoading={partnerSyncing}
                        syncLabel={
                            partnerSyncState === 'match'
                                ? 'Synced'
                                : partnerSyncState === 'mismatch'
                                  ? 'Update opportunity'
                                  : 'Sync partner'
                        }
                    />
                </>
            ) : null}
        </article>
    );
};

export default PreContractProductDealCard;
